import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../util/reactIntl';
import classNames from 'classnames';
import { isAgeConfirmed } from '../../../util/signupProtectedData';

import {
  getSignupStepsForRole,
  getTotalSteps,
  getProgressPercentage,
  STEP_EMAIL,
  STEP_PASSWORD,
  STEP_PROFILE,
  STEP_VERIFY,
} from './signupStepsConfig';

import SignupProgressBar from './SignupProgressBar';
import SignupStepHeader from './SignupStepHeader';
import EmailStep from './steps/EmailStep';
import PasswordStep from './steps/PasswordStep';
import ProfileStep from './steps/ProfileStep';
import VerifyEmailStep from './steps/VerifyEmailStep';

import css from './SignupWizard.module.css';

class SignupWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStepIndex: 0,
      formData: {
        email: props.initialEmail || '',
        password: '',
        firstName: '',
        lastName: '',
        organizationName: '',
        ageConfirmed: [],
        phoneNumber: '',
      },
    };

    this.handleNextStep = this.handleNextStep.bind(this);
    this.handlePrevStep = this.handlePrevStep.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleFieldChange(fieldName, value) {
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [fieldName]: value,
      },
    }));
  }

  handleNextStep(stepData) {
    const { role } = this.props;
    const steps = getSignupStepsForRole(role);
    const currentStep = steps[this.state.currentStepIndex];

    // Update form data with step data
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        ...stepData,
      },
    }));

    // Check if this step creates the account
    if (currentStep.isAccountCreation) {
      this.handleSubmit({ ...this.state.formData, ...stepData });
    } else {
      // Move to next step
      this.setState(prevState => ({
        currentStepIndex: prevState.currentStepIndex + 1,
      }));
    }
  }

  handlePrevStep(targetStepIndex = null) {
    this.setState(prevState => {
      // Calculate the target step (either specified or previous step)
      const newStepIndex =
        targetStepIndex !== null ? targetStepIndex : Math.max(0, prevState.currentStepIndex - 1);

      // If going back to email step (step 0), reset email so user can re-enter
      if (newStepIndex === 0) {
        return {
          currentStepIndex: newStepIndex,
          formData: {
            ...prevState.formData,
            email: '',
          },
        };
      }

      // For other steps, preserve all data
      return {
        currentStepIndex: newStepIndex,
      };
    });
  }

  handleSubmit(formData) {
    const { onSubmit, role } = this.props;

    // Prepare signup params. `newsletterOptIn` (beta-mode only) is
    // carried through so Auth.duck can add the new account to the
    // Brevo newsletter list right after the community list add.
    const signupParams = {
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      organizationName: formData.organizationName,
      ageConfirmed: isAgeConfirmed(formData.ageConfirmed),
      phoneNumber: formData.phoneNumber,
      role,
      // FieldCheckbox multi-checkbox: value lands in formData as an
      // array of selected values, so we coerce to a real boolean
      // before handing off to the signup thunk.
      newsletterOptIn:
        Array.isArray(formData.newsletterOptIn) && formData.newsletterOptIn.length > 0,
    };

    onSubmit(signupParams);
  }

  renderStep() {
    const {
      role,
      authInProgress,
      onOpenTermsOfService,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      currentUser,
      socialLoginButtons,
      loginLink,
      title,
      inviteContent,
      initialEmail,
      emailLocked,
    } = this.props;
    const { currentStepIndex, formData } = this.state;
    const steps = getSignupStepsForRole(role);
    const currentStep = steps[currentStepIndex];

    const commonProps = {
      formData,
      onSubmit: this.handleNextStep,
      onFieldChange: this.handleFieldChange,
      inProgress: authInProgress,
      onOpenTermsOfService,
    };

    switch (currentStep.id) {
      case STEP_EMAIL:
        return (
          <EmailStep
            {...commonProps}
            title={title}
            inviteContent={inviteContent}
            socialLoginButtons={socialLoginButtons}
            loginLink={loginLink}
            emailLocked={emailLocked}
            lockedEmail={initialEmail}
          />
        );

      case STEP_PASSWORD:
        return <PasswordStep {...commonProps} />;

      case STEP_PROFILE:
        return <ProfileStep {...commonProps} role={role} />;

      case STEP_VERIFY:
        return (
          <VerifyEmailStep
            formData={formData}
            currentUser={currentUser}
            onResendVerificationEmail={onResendVerificationEmail}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
            onSkip={this.props.onSkipVerification}
          />
        );

      default:
        return null;
    }
  }

  render() {
    const { role, className, signupError } = this.props;
    const { currentStepIndex } = this.state;
    const steps = getSignupStepsForRole(role);
    const currentStep = steps[currentStepIndex];
    const totalSteps = getTotalSteps(role);
    const progress = getProgressPercentage(currentStep.stepNumber, totalSteps);

    const classes = classNames(css.root, className);

    // Don't show header for email step (step 0)
    const showStepHeader = currentStep.stepNumber > 0;

    return (
      <div className={classes}>
        {showStepHeader && (
          <>
            <SignupProgressBar progress={progress} />
            <SignupStepHeader
              stepNumber={currentStep.stepNumber}
              titleKey={currentStep.titleKey}
              onBack={() => this.handlePrevStep()}
            />
          </>
        )}

        {signupError && (
          <div className={css.error}>
            {signupError.apiErrors?.[0]?.code === 'email-taken' ? (
              <FormattedMessage id="AuthenticationPage.signupFailedEmailTaken" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupFailed" />
            )}
          </div>
        )}

        {this.renderStep()}
      </div>
    );
  }
}

SignupWizard.defaultProps = {
  className: null,
  initialEmail: '',
  emailLocked: false,
  signupError: null,
  currentUser: null,
  sendVerificationEmailInProgress: false,
  title: null,
  inviteContent: null,
};

SignupWizard.propTypes = {
  className: PropTypes.string,
  role: PropTypes.oneOf(['audience', 'creator', 'organization']).isRequired,
  initialEmail: PropTypes.string,
  emailLocked: PropTypes.bool,
  authInProgress: PropTypes.bool.isRequired,
  signupError: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onOpenTermsOfService: PropTypes.func.isRequired,
  onResendVerificationEmail: PropTypes.func,
  onSkipVerification: PropTypes.func,
  sendVerificationEmailInProgress: PropTypes.bool,
  currentUser: PropTypes.object,
  socialLoginButtons: PropTypes.node,
  loginLink: PropTypes.node,
  title: PropTypes.node,
  inviteContent: PropTypes.node,
};

export default SignupWizard;
